import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Crown, Users, Zap } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/Auth/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { useFlutterwave } from 'flutterwave-react-v3';
import { subscriptionService } from '@/utils/subscriptionService';
import { FLW_PUBLIC_KEY } from '@/utils/constants';
import { useOrganization } from '@/hooks/useOrganization';

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  billing_interval: string;
  features: any;
  is_active: boolean;
}

interface GroupedPlans {
  [planName: string]: {
    [interval: string]: SubscriptionPlan;
  };
}

interface SubscriptionUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlan: any;
  onUpgrade: () => void;
  focusPlanName?: 'Individual' | 'Corporate' | 'Enterprise';
}

// Default placeholder config for the hook (required at top level)
const DEFAULT_FLUTTERWAVE_CONFIG = {
  public_key: FLW_PUBLIC_KEY || '',
  tx_ref: 'placeholder-ref',
  amount: 1,
  currency: 'USD',
  payment_options: 'card,banktransfer,ussd',
  customer: {
    email: 'placeholder@example.com',
    name: 'User',
    phone_number: '',
  },
  customizations: {
    title: 'Subscription Upgrade',
    description: 'Upgrade your plan',
    logo: '/favicon.ico',
  },
};

export const SubscriptionUpgradeModal: React.FC<SubscriptionUpgradeModalProps> = ({
  isOpen,
  onClose,
  currentPlan,
  onUpgrade,
  focusPlanName
}) => {
  const [plans, setPlans] = useState<GroupedPlans>({});
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [selectedInterval, setSelectedInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [paymentConfig, setPaymentConfig] = useState<any>(null);
  const [pendingPlan, setPendingPlan] = useState<SubscriptionPlan | null>(null);
  const [userHasUsedTrial, setUserHasUsedTrial] = useState(false);
  const [upgradingPlanId, setUpgradingPlanId] = useState<string | null>(null);

  const { user } = useAuth();
  const { isOrganization, loading: orgLoading } = useOrganization();
  const { toast } = useToast();
  const normalizedFocusPlan = focusPlanName?.toLowerCase();

  // ✅ Hook called at the top level — always, unconditionally
  const handleFlutterPayment = useFlutterwave(paymentConfig || DEFAULT_FLUTTERWAVE_CONFIG);

  // ✅ Trigger payment only when a real config has been set
  useEffect(() => {
    if (!paymentConfig || !pendingPlan) return;

    handleFlutterPayment({
      callback: async (response: any) => {
        console.log('Flutterwave response:', response);

        // Reset config after use to avoid re-triggering
        setPaymentConfig(null);

        if (response.status === 'successful') {
          setLoading(true);
          try {
            const result = await subscriptionService.updateSubscription(pendingPlan.id);

            if (!result.success) {
              console.error('Subscription update failed:', result.error);
              throw new Error(result.error || 'Failed to update subscription');
            }

            console.log('Subscription updated successfully via service');

            // Clear subscription cache to refresh access control
            subscriptionService.clearCache();

            toast({
              title: 'Success',
              description: `Successfully upgraded to ${pendingPlan.name} ${pendingPlan.billing_interval} plan!`,
            });

            setPendingPlan(null);
            onUpgrade();
          } catch (error) {
            console.error('Error upgrading subscription:', error);
            toast({
              title: 'Error',
              description: 'Payment successful but failed to update subscription. Please contact support.',
              variant: 'destructive'
            });
          } finally {
            setLoading(false);
          }
        } else {
          toast({
            title: 'Payment Cancelled',
            description: 'Payment was not completed.',
            variant: 'destructive'
          });
        }
      },
      onClose: () => {
        console.log('Payment modal closed');
        setPaymentConfig(null);
      },
    });
  }, [paymentConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpen) {
      fetchPlans();
    }
  }, [isOpen, isOrganization, normalizedFocusPlan]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('name, billing_interval', { ascending: true });

      if (error) throw error;

      // Check if user has ever had a subscription record that was a trial
      if (user?.id) {
        const { data: pastSubscriptions, error: subError } = await supabase
          .from('user_subscriptions')
          .select('status, trial_ends_at')
          .eq('user_id', user.id);
        
        if (!subError && pastSubscriptions) {
          const usedTrial = pastSubscriptions.some(sub => sub.status === 'trial' || sub.trial_ends_at !== null);
          setUserHasUsedTrial(usedTrial);
        }
      }

      // Group plans by name and organize by monthly/yearly
      const groupedPlans = data?.reduce((acc: any, plan) => {
        if (!acc[plan.name]) {
          acc[plan.name] = {};
        }
        acc[plan.name][plan.billing_interval] = plan;
        return acc;
      }, {}) || {};

      // Sort plans based on account type preference.
      const sortedPlans: GroupedPlans = {};
      let planOrder = isOrganization
        ? ['Corporate', 'Enterprise', 'Individual']
        : ['Individual', 'Enterprise', 'Corporate'];

      // When a focused plan is requested (e.g. Enterprise), place it first.
      if (normalizedFocusPlan) {
        const focusPlan = planOrder.find((name) => name.toLowerCase() === normalizedFocusPlan);
        if (focusPlan) {
          planOrder = [focusPlan, ...planOrder.filter((name) => name !== focusPlan)];
        }
      }

      planOrder.forEach(planName => {
        if (groupedPlans[planName]) {
          sortedPlans[planName] = groupedPlans[planName];
        }
      });

      setPlans(sortedPlans);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast({
        title: 'Error',
        description: 'Failed to load subscription plans',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const buildFlutterwaveConfig = (plan: SubscriptionPlan) => {
    const txRef = `sub-upgrade-${user?.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const userEmail = user?.email || '';
    const userName = user?.user_metadata?.full_name || user?.user_metadata?.name || userEmail || 'User';

    if (!userEmail) {
      throw new Error('User email is required for payment');
    }

    if (!plan.price || plan.price <= 0) {
      throw new Error('Invalid plan price');
    }

    return {
      public_key: FLW_PUBLIC_KEY,
      tx_ref: txRef,
      amount: plan.price,
      currency: plan.currency || 'USD',
      payment_options: 'card,banktransfer,ussd',
      customer: {
        email: userEmail,
        name: userName,
        phone_number: user?.user_metadata?.phone || '',
      },
      customizations: {
        title: 'Subscription Upgrade',
        description: `Upgrade to ${plan.name} (${plan.billing_interval})`,
        logo: '/favicon.ico',
      },
      meta: {
        plan_id: plan.id,
        plan_name: plan.name,
        billing_interval: plan.billing_interval,
        user_id: user?.id,
      },
    };
  };

  const handleUpgrade = async (plan?: SubscriptionPlan) => {
    const planToUpgrade = plan || selectedPlan;

    if (!planToUpgrade || !user?.id) {
      toast({
        title: 'Error',
        description: 'Please select a plan to upgrade to',
        variant: 'destructive'
      });
      return;
    }

    const hasTrial = planToUpgrade.features?.trial_period_days > 0 || planToUpgrade.trial_period_days > 0;
    const isTrialEligible = hasTrial && !userHasUsedTrial;

    // Zero-Commitment Free Trial Activation (Bypass Flutterwave)
    if (isTrialEligible) {
      setUpgradingPlanId(planToUpgrade.id);
      try {
        const result = await subscriptionService.updateSubscription(planToUpgrade.id, 'trial');

        if (!result.success) {
          throw new Error(result.error || 'Failed to activate free trial');
        }

        subscriptionService.clearCache();

        toast({
          title: 'Trial Activated!',
          description: `You have successfully started your 30-day free trial of the ${planToUpgrade.name} plan!`,
        });

        onUpgrade();
        onClose();
      } catch (error) {
        console.error('Error starting free trial:', error);
        toast({
          title: 'Activation Error',
          description: error instanceof Error ? error.message : 'Failed to start free trial. Please try again.',
          variant: 'destructive'
        });
      } finally {
        setUpgradingPlanId(null);
      }
      return;
    }

    if (!FLW_PUBLIC_KEY) {
      toast({
        title: 'Configuration Error',
        description: 'Payment gateway not configured. Please contact support.',
        variant: 'destructive'
      });
      return;
    }

    try {
      const config = buildFlutterwaveConfig(planToUpgrade);
      setPendingPlan(planToUpgrade); // Store for use in callback
      onClose();                     // Close dialog

      // Small delay to let dialog close before Flutterwave modal opens
      setTimeout(() => {
        window.scrollTo(0, 0);
        setPaymentConfig(config);    // ✅ This triggers the useEffect to open payment
      }, 200);
    } catch (error) {
      console.error('Error building payment config:', error);
      toast({
        title: 'Payment Error',
        description: error instanceof Error ? error.message : 'Failed to initialize payment. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const getPlanIcon = (planName: string) => {
    switch (planName.toLowerCase()) {
      case 'individual':
        return <Zap className="h-5 w-5" />;
      case 'corporate':
        return <Users className="h-5 w-5" />;
      case 'enterprise':
        return <Crown className="h-5 w-5" />;
      default:
        return <Zap className="h-5 w-5" />;
    }
  };

  const formatPrice = (price: number, currency: string, interval: string) => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    });
    return `${formatter.format(price)}/${interval}`;
  };

  const isCurrentPlan = (plan: SubscriptionPlan) => {
    if (!currentPlan?.subscription_plans) return false;
    const currentPlanName = currentPlan.subscription_plans.name;
    const currentPlanInterval = currentPlan.subscription_plans.billing_interval;
    return plan.name === currentPlanName && plan.billing_interval === currentPlanInterval;
  };

  const isPlanRestrictedForUserType = (planName: string) => {
    if (orgLoading) return false;
    return isOrganization
      ? planName.toLowerCase() === 'individual'
      : planName.toLowerCase() === 'corporate';
  };

  const isPlanBlurredByFocus = (planName: string) => {
    if (!normalizedFocusPlan) return false;
    return planName.toLowerCase() !== normalizedFocusPlan;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5" />
            Upgrade Your Subscription
          </DialogTitle>
          <DialogDescription>
            Choose the plan that best fits your sustainability management needs
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          {Object.entries(plans).map(([planName, intervals]) => {
            const currentIntervalPlan = intervals[selectedInterval];
            const isCurrent = currentIntervalPlan && isCurrentPlan(currentIntervalPlan);
            const isRestricted = isPlanRestrictedForUserType(planName);
            const isBlurredByFocus = isPlanBlurredByFocus(planName);
            const isDisabledPlan = isRestricted || isBlurredByFocus;

            const hasTrial = currentIntervalPlan && (currentIntervalPlan.trial_period_days > 0 || currentIntervalPlan.features?.trial_period_days > 0);
            const isTrialEligible = hasTrial && !userHasUsedTrial;

            const isThisPlanUpgrading = currentIntervalPlan && upgradingPlanId === currentIntervalPlan.id;
            const isAnyPlanUpgrading = upgradingPlanId !== null || loading;

            return (
              <Card
                key={planName}
                className={`relative transition-all ${
                  selectedPlan?.name === planName
                    ? 'ring-2 ring-blue-500 border-blue-500'
                    : 'hover:shadow-lg'
                } ${isCurrent ? 'opacity-75' : ''} ${isDisabledPlan ? 'opacity-60' : ''}`}
              >
                {isCurrent && (
                  <Badge className="absolute -top-2 -right-2 bg-green-500">
                    Current Plan
                  </Badge>
                )}
                {isTrialEligible && !isCurrent && (
                  <Badge className="absolute -top-2 -left-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white animate-pulse">
                    30-Day Free Trial
                  </Badge>
                )}
                {normalizedFocusPlan && !isBlurredByFocus && !isTrialEligible && (
                  <Badge className="absolute -top-2 -left-2 bg-blue-600 text-white">
                    Recommended
                  </Badge>
                )}
                {isRestricted && (
                  <Badge variant="secondary" className="absolute -top-2 -left-2">
                    Restricted
                  </Badge>
                )}

                <CardHeader className={`text-center ${isDisabledPlan ? 'blur-[1px]' : ''}`}>
                  <div className="flex justify-center mb-2">
                    {getPlanIcon(planName)}
                  </div>
                  <CardTitle className="text-xl">{planName}</CardTitle>
                  <CardDescription>
                    {intervals.monthly?.description || intervals.yearly?.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className={isDisabledPlan ? 'blur-[1px]' : ''}>
                  {/* Billing Interval Toggle */}
                  <div className="flex justify-center mb-4">
                    <div className="flex bg-gray-100 rounded-lg p-1">
                      <button
                        type="button"
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                          selectedInterval === 'monthly'
                            ? 'bg-white text-blue-600 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                        onClick={() => setSelectedInterval('monthly')}
                      >
                        Monthly
                      </button>
                      <button
                        type="button"
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                          selectedInterval === 'yearly'
                            ? 'bg-white text-blue-600 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                        onClick={() => setSelectedInterval('yearly')}
                      >
                        Yearly
                        {intervals.yearly?.features?.yearly_discount && (
                          <span className="ml-1 text-xs bg-green-500 text-white px-1 py-0.5 rounded">
                            {intervals.yearly.features.discount_percentage}%
                          </span>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Price Display */}
                  <div className="text-center mb-4">
                    <div className="text-2xl font-bold text-blue-600">
                      {intervals[selectedInterval] && formatPrice(
                        intervals[selectedInterval].price,
                        intervals[selectedInterval].currency,
                        selectedInterval
                      )}
                    </div>
                    {selectedInterval === 'yearly' && intervals.yearly?.features?.yearly_discount && (
                      <div className="text-xs text-green-600 mt-1">
                        Save {intervals.yearly.features.discount_percentage}% with yearly billing
                      </div>
                    )}
                  </div>

                  {/* Features */}
                  <div className="space-y-2 mb-6">
                    {intervals[selectedInterval]?.features && typeof intervals[selectedInterval].features === 'object' && (
                      <>
                        {intervals[selectedInterval].features.modules &&
                          Array.isArray(intervals[selectedInterval].features.modules) &&
                          intervals[selectedInterval].features.modules.map((module: string) => (
                            <div key={module} className="flex items-start gap-2">
                              <Check className="h-3 w-3 text-green-500 flex-shrink-0 mt-0.5" />
                              <span className="text-xs leading-relaxed">
                                {module.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Access
                              </span>
                            </div>
                          ))
                        }

                        {intervals[selectedInterval].features.max_collaborators && (
                          <div className="flex items-start gap-2">
                            <Check className="h-3 w-3 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-xs leading-relaxed">
                              No of Maximum Collaborators = {intervals[selectedInterval].features.max_collaborators}
                            </span>
                          </div>
                        )}

                        {intervals[selectedInterval].features.dedicated_support && (
                          <div className="flex items-start gap-2">
                            <Check className="h-3 w-3 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-xs leading-relaxed">Dedicated support</span>
                          </div>
                        )}

                        {intervals[selectedInterval].features.more_collaborators_on_demand && (
                          <div className="flex items-start gap-2">
                            <Check className="h-3 w-3 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-xs leading-relaxed">More Collaborators possible on demand</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Action Button */}
                  <div className="mt-6">
                    {isCurrent ? (
                      <Button disabled className="w-full text-sm">
                        Current Plan
                      </Button>
                    ) : isDisabledPlan ? (
                      <Button disabled variant="outline" className="w-full text-sm">
                        {isBlurredByFocus ? `Choose ${focusPlanName}` : 'Not Available For Your Account Type'}
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handleUpgrade(intervals[selectedInterval])}
                        disabled={isThisPlanUpgrading}
                        className="w-full text-sm bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium"
                      >
                        {isThisPlanUpgrading ? 'Processing...' : (isTrialEligible ? 'Start 30-Day Free Trial' : `Upgrade to ${planName}`)}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};







// import React, { useState, useEffect } from 'react';
// import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
// import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
// import { Button } from '@/components/ui/button';
// import { Badge } from '@/components/ui/badge';
// import { Check, Crown, Users, Zap } from 'lucide-react';
// import { supabase } from '@/integrations/supabase/client';
// import { useAuth } from '@/components/Auth/AuthProvider';
// import { useToast } from '@/hooks/use-toast';
// import { useFlutterwave } from 'flutterwave-react-v3';
// import { subscriptionService } from '@/utils/subscriptionService';
// import { FLW_PUBLIC_KEY } from '@/utils/constants';

// interface SubscriptionPlan {
//   id: string;
//   name: string;
//   description: string;
//   price: number;
//   currency: string;
//   billing_interval: string;
//   features: any;
//   is_active: boolean;
// }

// interface GroupedPlans {
//   [planName: string]: {
//     [interval: string]: SubscriptionPlan;
//   };
// }

// interface SubscriptionUpgradeModalProps {
//   isOpen: boolean;
//   onClose: () => void;
//   currentPlan: any;
//   onUpgrade: () => void;
// }

// export const SubscriptionUpgradeModal: React.FC<SubscriptionUpgradeModalProps> = ({
//   isOpen,
//   onClose,
//   currentPlan,
//   onUpgrade
// }) => {
//   const [plans, setPlans] = useState<GroupedPlans>({});
//   const [loading, setLoading] = useState(false);
//   const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
//   const [selectedInterval, setSelectedInterval] = useState<'monthly' | 'yearly'>('monthly');
//   const { user } = useAuth();
//   const { toast } = useToast();

//   const getFlutterwaveConfig = (plan: SubscriptionPlan) => {
//     // Generate unique transaction reference
//     const txRef = `sub-upgrade-${user?.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
//     // Get user details with fallbacks
//     const userEmail = user?.email || '';
//     const userName = user?.user_metadata?.full_name || user?.user_metadata?.name || userEmail || 'User';
    
//     // Validate required fields
//     if (!userEmail) {
//       throw new Error('User email is required for payment');
//     }
    
//     if (!plan.price || plan.price <= 0) {
//       throw new Error('Invalid plan price');
//     }

//     return {
//       public_key: FLW_PUBLIC_KEY,
//       tx_ref: txRef,
//       amount: plan.price,
//       currency: plan.currency || 'USD', // Default to USD instead of NGN
//       payment_options: 'card,banktransfer,ussd',
//       customer: {
//         email: userEmail,
//         name: userName,
//         phone_number: user?.user_metadata?.phone || '',
//       },
//       customizations: {
//         title: 'Subscription Upgrade',
//         description: `Upgrade to ${plan.name} (${plan.billing_interval})`,
//         logo: '/favicon.ico',
//       },
//       meta: {
//         plan_id: plan.id,
//         plan_name: plan.name,
//         billing_interval: plan.billing_interval,
//         user_id: user?.id,
//       },
//     };
//   };

//   useEffect(() => {
//     if (isOpen) {
//       fetchPlans();
//     }
//   }, [isOpen]);

//   const fetchPlans = async () => {
//     try {
//       const { data, error } = await supabase
//         .from('subscription_plans')
//         .select('*')
//         .eq('is_active', true)
//         .order('name, billing_interval', { ascending: true });

//       if (error) throw error;
      
//       // Group plans by name and organize by monthly/yearly
//       const groupedPlans = data?.reduce((acc: any, plan) => {
//         if (!acc[plan.name]) {
//           acc[plan.name] = {};
//         }
//         acc[plan.name][plan.billing_interval] = plan;
//         return acc;
//       }, {}) || {};
      
//       // Sort plans to ensure Individual comes first
//       const sortedPlans: GroupedPlans = {};
//       const planOrder = ['Individual', 'Corporate', 'Enterprise'];
      
//       planOrder.forEach(planName => {
//         if (groupedPlans[planName]) {
//           sortedPlans[planName] = groupedPlans[planName];
//         }
//       });
      
//       setPlans(sortedPlans);
//     } catch (error) {
//       console.error('Error fetching plans:', error);
//       toast({
//         title: 'Error',
//         description: 'Failed to load subscription plans',
//         variant: 'destructive'
//       });
//     }
//   };

//   const handleUpgrade = async (plan?: SubscriptionPlan) => {
// console.log(7777, plan);
// console.log(2122,FLW_PUBLIC_KEY)
// console.log(43422, getFlutterwaveConfig(plan || selectedPlan!))
  
//     const planToUpgrade = plan || selectedPlan;
//     if (!planToUpgrade || !user?.id) {
//       toast({
//         title: 'Error',
//         description: 'Please select a plan to upgrade to',
//         variant: 'destructive'
//       });
//       return;
//     }

//     // Validate Flutterwave public key
//     if (!FLW_PUBLIC_KEY) {
//       toast({
//         title: 'Configuration Error',
//         description: 'Payment gateway not configured. Please contact support.',
//         variant: 'destructive'
//       });
//       return;
//     }

//     onClose(); // Close your dialog first

//     setTimeout(() => {
//       window.scrollTo(0, 0); // Scroll to top before opening modal
      
//       try {
//         // Now open Flutterwave modal
        
//         const config = getFlutterwaveConfig(planToUpgrade);
//         console.log(123,config);
//         const handleFlutterPayment = useFlutterwave(config);
        
//         handleFlutterPayment({
//           callback: async (response: any) => {
//             console.log('Flutterwave response:', response);
            
//             if (response.status === 'successful') {
//               setLoading(true);
//               try {
//                 // Use the subscription service to update the subscription
//                 const result = await subscriptionService.updateSubscription(planToUpgrade.id);
                
//                 if (!result.success) {
//                   console.error('Subscription update failed:', result.error);
//                   throw new Error(result.error || 'Failed to update subscription');
//                 }

//                 console.log('Subscription updated successfully via service');

//                 // Clear subscription cache to refresh access control
//                 subscriptionService.clearCache();

//                 toast({
//                   title: 'Success',
//                   description: `Successfully upgraded to ${planToUpgrade.name} ${selectedInterval} plan!`,
//                 });

//                 onUpgrade();
//               } catch (error) {
//                 console.error('Error upgrading subscription:', error);
//                 toast({
//                   title: 'Error',
//                   description: 'Payment successful but failed to update subscription. Please contact support.',
//                   variant: 'destructive'
//                 });
//               } finally {
//                 setLoading(false);
//               }
//             } else {
//               toast({
//                 title: 'Payment Cancelled',
//                 description: 'Payment was not completed.',
//                 variant: 'destructive'
//               });
//             }
//           },
//           onClose: () => {
//             console.log('Payment modal closed');
//           },
//         });
//       } catch (error) {
//         console.error('Error initializing payment:', error);
//         toast({
//           title: 'Payment Error',
//           description: 'Failed to initialize payment. Please try again.',
//           variant: 'destructive'
//         });
//       }
//     }, 200); // 200ms delay
//   };

//   const getPlanIcon = (planName: string) => {
//     switch (planName.toLowerCase()) {
//       case 'individual':
//         return <Zap className="h-5 w-5" />;
//       case 'corporate':
//         return <Users className="h-5 w-5" />;
//       case 'enterprise':
//         return <Crown className="h-5 w-5" />;
//       default:
//         return <Zap className="h-5 w-5" />;
//     }
//   };

//   const formatPrice = (price: number, currency: string, interval: string) => {
//     const formatter = new Intl.NumberFormat('en-US', {
//       style: 'currency',
//       currency: currency || 'USD'
//     });
//     return `${formatter.format(price)}/${interval}`;
//   };

//   const isCurrentPlan = (plan: SubscriptionPlan) => {
//     // Check if this plan matches the current user's plan
//     if (!currentPlan?.subscription_plans) return false;
    
//     // Compare plan IDs
//     const currentPlanId = currentPlan.subscription_plans.id;
//     const currentPlanName = currentPlan.subscription_plans.name;
//     const currentPlanInterval = currentPlan.subscription_plans.billing_interval;
    
//     // Match by plan name and billing interval
//     return plan.name === currentPlanName && plan.billing_interval === currentPlanInterval;
//   };

//   const isUpgrade = (plan: SubscriptionPlan) => {
//     const currentPrice = currentPlan?.subscription_plans?.price || 0;
//     return plan.price > currentPrice;
//   };

//   return (
//     <Dialog open={isOpen} onOpenChange={onClose}>
//       <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
//         <DialogHeader>
//           <DialogTitle className="flex items-center gap-2">
//             <Crown className="h-5 w-5" />
//             Upgrade Your Subscription
//           </DialogTitle>
//           <DialogDescription>
//             Choose the plan that best fits your sustainability management needs
//           </DialogDescription>
//         </DialogHeader>

//         <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
//           {Object.entries(plans).map(([planName, intervals]) => {
//             const currentIntervalPlan = intervals[selectedInterval];
//             const isCurrent = currentIntervalPlan && isCurrentPlan(currentIntervalPlan);
            
//             return (
//               <Card 
//                 key={planName}
//                 className={`relative transition-all ${
//                   selectedPlan?.name === planName 
//                     ? 'ring-2 ring-blue-500 border-blue-500' 
//                     : 'hover:shadow-lg'
//                 } ${isCurrent ? 'opacity-75' : ''}`}
//               >
//                 {isCurrent && (
//                   <Badge className="absolute -top-2 -right-2 bg-green-500">
//                     Current Plan
//                   </Badge>
//                 )}
                
//                 <CardHeader className="text-center">
//                   <div className="flex justify-center mb-2">
//                     {getPlanIcon(planName)}
//                   </div>
//                   <CardTitle className="text-xl">{planName}</CardTitle>
//                   <CardDescription>
//                     {intervals.monthly?.description || intervals.yearly?.description}
//                   </CardDescription>
//                 </CardHeader>

//                 <CardContent>
//                   {/* Billing Interval Toggle */}
//                   <div className="flex justify-center mb-4">
//                     <div className="flex bg-gray-100 rounded-lg p-1">
//                       <button
//                         type="button"
//                         className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
//                           selectedInterval === 'monthly'
//                             ? 'bg-white text-blue-600 shadow-sm'
//                             : 'text-gray-600 hover:text-gray-900'
//                         }`}
//                         onClick={() => setSelectedInterval('monthly')}
//                       >
//                         Monthly
//                       </button>
//                       <button
//                         type="button"
//                         className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
//                           selectedInterval === 'yearly'
//                             ? 'bg-white text-blue-600 shadow-sm'
//                             : 'text-gray-600 hover:text-gray-900'
//                         }`}
//                         onClick={() => setSelectedInterval('yearly')}
//                       >
//                         Yearly
//                         {intervals.yearly?.features?.yearly_discount && (
//                           <span className="ml-1 text-xs bg-green-500 text-white px-1 py-0.5 rounded">
//                             {intervals.yearly.features.discount_percentage}%
//                           </span>
//                         )}
//                       </button>
//                     </div>
//                   </div>

//                   {/* Price Display */}
//                   <div className="text-center mb-4">
//                     <div className="text-2xl font-bold text-blue-600">
//                       {intervals[selectedInterval] && formatPrice(
//                         intervals[selectedInterval].price,
//                         intervals[selectedInterval].currency,
//                         selectedInterval
//                       )}
//                     </div>
//                     {selectedInterval === 'yearly' && intervals.yearly?.features?.yearly_discount && (
//                       <div className="text-xs text-green-600 mt-1">
//                         Save {intervals.yearly.features.discount_percentage}% with yearly billing
//                       </div>
//                     )}
//                   </div>

//                   {/* Features */}
//                   <div className="space-y-2 mb-6">
//                     {intervals[selectedInterval]?.features && typeof intervals[selectedInterval].features === 'object' && (
//                       <>
//                         {/* Display modules as individual features */}
//                         {intervals[selectedInterval].features.modules && Array.isArray(intervals[selectedInterval].features.modules) && 
//                           intervals[selectedInterval].features.modules.map((module: string) => (
//                             <div key={module} className="flex items-start gap-2">
//                               <Check className="h-3 w-3 text-green-500 flex-shrink-0 mt-0.5" />
//                               <span className="text-xs leading-relaxed">
//                                 {module.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Access
//                               </span>
//                             </div>
//                           ))
//                         }
                        
//                         {/* Display max collaborators */}
//                         {intervals[selectedInterval].features.max_collaborators && (
//                           <div className="flex items-start gap-2">
//                             <Check className="h-3 w-3 text-green-500 flex-shrink-0 mt-0.5" />
//                             <span className="text-xs leading-relaxed">
//                               No of Maximum Collaborators = {intervals[selectedInterval].features.max_collaborators}
//                             </span>
//                           </div>
//                         )}
                        
//                         {/* Display dedicated support */}
//                         {intervals[selectedInterval].features.dedicated_support && (
//                           <div className="flex items-start gap-2">
//                             <Check className="h-3 w-3 text-green-500 flex-shrink-0 mt-0.5" />
//                             <span className="text-xs leading-relaxed">Dedicated support</span>
//                           </div>
//                         )}
                        
//                         {/* Display more collaborators on demand */}
//                         {intervals[selectedInterval].features.more_collaborators_on_demand && (
//                           <div className="flex items-start gap-2">
//                             <Check className="h-3 w-3 text-green-500 flex-shrink-0 mt-0.5" />
//                             <span className="text-xs leading-relaxed">More Collaborators possible on demand</span>
//                           </div>
//                         )}
//                       </>
//                     )}
//                   </div>

//                   {/* Action Button */}
//                   <div className="mt-6">
//                     {isCurrent ? (
//                       <Button disabled className="w-full text-sm">
//                         Current Plan
//                       </Button>
//                     ) : (
//                       <Button 
//                         onClick={() => handleUpgrade(intervals[selectedInterval])}
//                         disabled={loading}
//                         className="w-full text-sm"
//                       >
//                         {loading ? 'Upgrading...' : `Upgrade to ${planName}`}
//                       </Button>
//                     )}
//                   </div>
//                 </CardContent>
//               </Card>
//             );
//           })}
//         </div>

//         <div className="flex justify-end gap-2 mt-6">
//           <Button variant="outline" onClick={onClose}>
//             Cancel
//           </Button>
//         </div>
//       </DialogContent>
//     </Dialog>
//   );
// }; 
